import { observer } from 'mobx-react-lite'
import { useEffect, useRef } from 'react'

import { useRunState } from '../provider/useRunState'
import { store } from '@/src/stores/store'

/**
 * Connects run lifecycle and player input to `store.tutorial`.
 * Mount once inside `RunStateProvider` (e.g. from `GameHud`).
 */
export const TutorialController = observer(function TutorialController() {
    const runState = useRunState()
    const previousLaneIndexRef = useRef(runState.snapshot.laneIndex)
    const previousJumpingRef = useRef(runState.snapshot.isJumping)

    useEffect(() => {
        if (!store.tutorialEnabled) {
            store.tutorial.reset()
            return
        }

        if (runState.phase === 'running' && !store.tutorial.active) {
            store.tutorial.startRun()
            previousLaneIndexRef.current = runState.snapshot.laneIndex
            previousJumpingRef.current = runState.snapshot.isJumping
            return
        }

        if (runState.phase === 'idle') {
            store.tutorial.reset()
        }
    }, [runState.phase, store.tutorialEnabled])

    useEffect(() => {
        if (!store.tutorialEnabled || !store.powerUpTutorial.hasSeenAllOverlays) return
        store.markTutorialPlayed()
    }, [store.powerUpTutorial.hasSeenAllOverlays, store.tutorialEnabled])

    useEffect(() => {
        if (!store.tutorialEnabled || !store.tutorial.active) return
        if (runState.phase !== 'running') return

        const previousLaneIndex = previousLaneIndexRef.current
        const laneIndex = runState.snapshot.laneIndex
        store.tutorial.syncLaneIndex(laneIndex)

        if (laneIndex < previousLaneIndex) {
            store.tutorial.notifyMoveLeft()
        } else if (laneIndex > previousLaneIndex) {
            store.tutorial.notifyMoveRight()
        }

        previousLaneIndexRef.current = laneIndex
    }, [runState.snapshot.laneIndex, runState.phase, store.tutorialEnabled, store.tutorial.active])

    useEffect(() => {
        if (!store.tutorialEnabled || !store.tutorial.active) return
        if (runState.phase !== 'running') return

        const wasJumping = previousJumpingRef.current
        const isJumping = runState.snapshot.isJumping

        if (!wasJumping && isJumping) {
            store.tutorial.notifyJump()
        }

        previousJumpingRef.current = isJumping
    }, [runState.snapshot.isJumping, runState.phase, store.tutorialEnabled, store.tutorial.active])

    useEffect(() => {
        if (!store.tutorialEnabled || !store.tutorial.active) return
        if (runState.phase !== 'running') return

        const snapAvailable = runState.snapshot.batteryCount > 0
        store.tutorial.setSnapHintAvailable(snapAvailable)
    }, [
        runState.snapshot.batteryCount,
        runState.phase,
        store.tutorialEnabled,
        store.tutorial.active,
    ])

    useEffect(() => {
        if (!store.tutorialEnabled || !store.tutorial.active) return

        function onSnapTrigger() {
            store.tutorial.notifySnap()
        }

        window.addEventListener('snap-trigger', onSnapTrigger)
        return () => window.removeEventListener('snap-trigger', onSnapTrigger)
    }, [store.tutorialEnabled, store.tutorial.active])

    return null
})
